import { SitePR } from "@inrange/building-manager-api-client";
import { useState } from "react";
import { Alert, Button, Offcanvas, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import PRExplainModal from "./PRExplainModal";
import { RYG_ICONS } from "./deploymentStatsUtils";

const formatPR = (pct: number | null): string => {
  if (pct === null) return "N/A";
  return `${pct.toFixed(1)}%`;
};

const PRBreakdownPanel = ({
  sites,
  aggregatePR,
  onClose,
  title,
  mode = "gpr",
}: {
  sites: SitePR[];
  aggregatePR: { lastMonth: { pct: number | null; ryg: string | null }; last12Months: { pct: number | null; ryg: string | null } };
  onClose: () => void;
  title?: string;
  mode?: "gpr" | "opr";
}) => {
  type SortField =
    | "site"
    | "lastMonth"
    | "last12Months"
    | "contracted"
    | "months"
    | "latestMonth"
    | "curtailmentLossMwh";
  const [sortField, setSortField] = useState<SortField>("last12Months");
  const [sortAsc, setSortAsc] = useState(true);
  const [showAlert, setShowAlert] = useState(true);
  const [showExplain, setShowExplain] = useState(false);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev);
    } else {
      setSortField(field);
      setSortAsc(field === "site" || field === "latestMonth");
    }
  };

  // OPR mode reads `oprLastMonth` / `oprLast12Months`; GPR mode reads the
  // legacy `lastMonth` / `last12Months` fields.
  const fieldFor = (s: SitePR, period: "last12Months" | "lastMonth") => {
    if (mode === "opr") {
      return period === "last12Months" ? s.oprLast12Months : s.oprLastMonth;
    }
    return period === "last12Months" ? s.last12Months : s.lastMonth;
  };
  const ratioName = mode === "opr" ? "OPR" : "GPR";

  // Sort with blue (no data) always after green: blue gets a sentinel
  // value that sorts below green (high pct) but above nothing.
  const rygOrder = (ryg: string | null | undefined) => {
    if (!ryg || ryg === "blue") return 999; // after green
    if (ryg === "green") return 3;
    if (ryg === "amber") return 2;
    return 1; // red
  };

  const contractedFor = (s: SitePR): number | null =>
    s.last12Months?.gprPct ?? s.lastMonth?.gprPct ?? null;

  const numCmp = (a: number, b: number) => (sortAsc ? a - b : b - a);
  const strCmp = (a: string, b: string) =>
    sortAsc ? a.localeCompare(b) : b.localeCompare(a);

  const sorted = [...sites].sort((a, b) => {
    if (sortField === "site") return strCmp(a.siteName, b.siteName);
    if (sortField === "contracted")
      return numCmp(contractedFor(a) ?? 999, contractedFor(b) ?? 999);
    if (sortField === "months")
      return numCmp(a.monthsAnalysed ?? 0, b.monthsAnalysed ?? 0);
    if (sortField === "latestMonth")
      return strCmp(a.latestMonthLabel ?? "", b.latestMonthLabel ?? "");
    if (sortField === "curtailmentLossMwh")
      return numCmp(a.curtailmentLossMwh ?? -1, b.curtailmentLossMwh ?? -1);
    // Period sort (lastMonth / last12Months) — RYG primary, pct secondary.
    const period = sortField;
    const aRyg = rygOrder(fieldFor(a, period)?.ryg);
    const bRyg = rygOrder(fieldFor(b, period)?.ryg);
    if (aRyg !== bRyg) return sortAsc ? aRyg - bRyg : bRyg - aRyg;
    const aVal = fieldFor(a, period)?.pct ?? -1;
    const bVal = fieldFor(b, period)?.pct ?? -1;
    return sortAsc ? aVal - bVal : bVal - aVal;
  });

  const headerStyle = { cursor: "pointer" as const };

  const arrow = (field: SortField) =>
    sortField === field ? (sortAsc ? " \u25B2" : " \u25BC") : "";

  return (
    <>
      <Offcanvas
        show
        placement="bottom"
        onHide={onClose}
        backdrop={false}
        scroll={true}
        enforceFocus={false}
        className="no-transition"
        style={{ height: "50vh" }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {title
              ? title
              : <>
                  {aggregatePR.last12Months.ryg &&
                    RYG_ICONS[aggregatePR.last12Months.ryg]}{" "}
                  Aggregate {ratioName}: {formatPR(aggregatePR.last12Months.pct)}
                </>}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ overflow: "auto", padding: "0 16px 16px" }}>
          {showAlert && (
            <div className="mb-2">
              <Alert
                variant="info"
                dismissible
                onClose={() => setShowAlert(false)}
                className="small"
              >
                <Button
                  variant="link"
                  className="p-0 text-muted"
                  onClick={() => setShowExplain(true)}
                >
                  How is this calculated?
                </Button>{" "}
                {mode === "opr"
                  ? "Operating PR (OPR) = Actual / Expected (using actual weather + InRange PV model). Green ≥ 90%, amber 85 – 90%, red < 85%. The aggregate and per-site RYGs use the same thresholds — OPR is graded as a fixed global ratio rather than against a per-site contract."
                  : "Guaranteed PR (GPR) aggregate RYG: green ≥ 80%, amber 75 – 80%, red < 75%. Per-site RYG instead reflects that site's contracted GPR (Contracted column) — green when measured GPR ≥ threshold, amber within 5pp below, red more than 5pp below. Sites without a configured GPR rule fall back to the fleet default; their Contracted column shows the fallback with a trailing * (e.g. 76.5%*)."}
                {" "}The Months column shows how many months of data are being
                used; for sites with an export limit this only includes
                months where readings have been committed for the full
                month — typically within a few days of month end.
              </Alert>
            </div>
          )}

          <Table striped hover size="sm">
            <thead>
              <tr>
                <th></th>
                <th style={headerStyle} onClick={() => toggleSort("site")}>
                  Site{arrow("site")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("lastMonth")}
                >
                  Last month{arrow("lastMonth")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("last12Months")}
                >
                  Last 12 months{arrow("last12Months")}
                </th>
                {mode !== "opr" && (
                  <th
                    className="text-end"
                    style={headerStyle}
                    onClick={() => toggleSort("contracted")}
                  >
                    Contracted{arrow("contracted")}
                  </th>
                )}
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("months")}
                >
                  Months{arrow("months")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("latestMonth")}
                >
                  Latest month{arrow("latestMonth")}
                </th>
                <th
                  className="text-end"
                  style={headerStyle}
                  onClick={() => toggleSort("curtailmentLossMwh")}
                >
                  Est curtailed MWh{arrow("curtailmentLossMwh")}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((site) => {
                const lm = fieldFor(site, "lastMonth");
                const l12 = fieldFor(site, "last12Months");
                // Pull contracted GPR from whichever period populated it.
                // OPR periods don't carry gprPct (fixed thresholds), so the
                // Contracted column is rendered only for the GPR variant.
                const contractedPct = l12?.gprPct ?? lm?.gprPct ?? null;
                const contractedIsDefault =
                  l12?.gprPctIsDefault ?? lm?.gprPctIsDefault ?? false;
                const contractedSuffix = contractedIsDefault ? "*" : "";
                return (
                  <tr key={site.siteId}>
                    <td style={{ width: "30px" }}>
                      {RYG_ICONS[l12?.ryg ?? "blue"] ?? RYG_ICONS.blue}
                    </td>
                    <td>
                      <Link to={`/site/${site.siteId}/health`}>
                        {site.siteName}
                      </Link>
                    </td>
                    <td className="text-end">
                      {lm?.ryg && RYG_ICONS[lm.ryg]}{" "}
                      {formatPR(lm?.pct ?? null)}
                    </td>
                    <td className="text-end">
                      {l12?.ryg && RYG_ICONS[l12.ryg]}{" "}
                      {formatPR(l12?.pct ?? null)}
                    </td>
                    {mode !== "opr" && (
                      <td className="text-end">
                        {contractedPct != null
                          ? `${contractedPct.toFixed(1)}%${contractedSuffix}`
                          : ""}
                      </td>
                    )}
                    <td className="text-end">{site.monthsAnalysed ?? ""}</td>
                    <td className="text-end">
                      {site.latestMonthLabel ?? ""}
                    </td>
                    <td className="text-end text-muted">
                      {site.curtailmentLossMwh != null
                        ? site.curtailmentLossMwh.toFixed(1)
                        : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Offcanvas.Body>
      </Offcanvas>
      <PRExplainModal
        show={showExplain}
        onHide={() => setShowExplain(false)}
      />
    </>
  );
};

export default PRBreakdownPanel;
